import { gql } from '@apollo/client';

export const AGENT_DELEGATION_FIELDS = gql`
  fragment AgentDelegationFields on AgentDelegation {
    delegationId
    kind
    status
    taskType
    localRuntime
    localModel
    localAgentProfile
    delegatedByUserId
    targetName
    mcpConnectionId
    jobId
    jobMode
    terminalHold {
      terminalSessionId
      deviceId
      heldAt
    }
    conversationId
    runId
    agentTemplateId
    skipApprovals
    statusMessage
    activity
    error
    errorCode
    logTailExcerpt
    executionLog {
      at
      kind
      status
      message
      attempt
      attachmentIds
    }
    pendingQuestion {
      text
      options
      askedAt
      isApproval
    }
    pendingApproval {
      message
      actions {
        id
        label
        toolName
        effect {
          recipients
          recipientCount
          target
          subject
          bodyPreview
          attachmentNames
          attachmentCount
          workspace
        }
      }
    }
    ledgerRef {
      conversationId
      runId
      msgIdx
    }
    blockedReason
    endedReason
    answeredInTextOnly
    prUrl
    plan
    # planHash is derived, never persisted: selecting any other field the API does not
    # expose in this fragment fails the whole query (WEB-P0.04).
    planHash
    prState
    prNumber
    prChecksStatus
    prChecksFailedCount
    prReviewDecision
    prReviewCommentCount
    prSignalsUpdatedAt
    startedAt
    finishedAt
    droppedAttachmentCount
  }
`;

// The API ships before the web build that selects `agentRuns` and `ticketArtifacts`: an API that
// predates them rejects any query naming them. Selected on every entry fragment, mutation
// responses included, because handleUpdateInboxEntry replaces the stored entry with the response.
// `repositorySlug` is sealed content the API sends only to the user who delegated the run.
export const TICKET_ARTIFACT_FIELDS = gql`
  fragment TicketArtifactFields on TicketArtifact {
    id
    createdAt
    updatedAt
    sourceRunId
    pullRequest {
      url
      repositorySlug
      number
      state
      checksStatus
      checksFailedCount
      reviewDecision
      reviewCommentCount
      signalsUpdatedAt
    }
  }
`;

// handleUpdateInboxEntry replaces the stored entry with whatever a mutation returns and
// keeps only communications, relatedInboxEntries, newReplies, chatActivity and
// taskSubgroupId from the old one, so every InboxEntry response selects at least these
// fields or the store blanks them.
export const INBOX_ENTRY_CORE_FIELDS = gql`
  fragment InboxEntryCoreFields on InboxEntry {
    id
    createdAt
    createdFor
    subject
    description
    participantIds
    isSuggestion
    taskGroupId
    taskSubgroupId
    orderKey
    parentInboxEntryId
    childOrderKey
    childProgress {
      total
      resolved
    }
    specKey
    specStatus
    specReviewers {
      userId
      approvedAt
    }
    requirementRef
    taskLabelIds
    relatedInboxEntryIds
    version
    deletedAt
    communicationIds
    dueAt
    resolvedAt
    resolutionSuggestedAt
    terminalSessionId
    terminalFolderName
    terminalBranch
    approvalDecision
    updatedAt
    priority
    entryKind
    executorAttention
    executorAttentionReasons
    flowApproval {
      executionId
      flowId
      ownerUserId
      pauseStepId
    }
    brainReview {
      teamId
      assertionId
      revision
    }
    brainSignalReview {
      teamId
      signalId
      version
      defId
    }
    agentDelegation {
      ...AgentDelegationFields
    }
    agentRuns {
      ...AgentDelegationFields
    }
    ticketArtifacts {
      ...TicketArtifactFields
    }
    checklists {
      id
      title
      subtasks {
        id
        title
        completed
      }
    }
  }
  ${AGENT_DELEGATION_FIELDS}
  ${TICKET_ARTIFACT_FIELDS}
`;
